/**
 * ProgressEstimator
 * ─────────────────
 * Indeterminate progress bar + elapsed-seconds counter + phase-aware
 * Arabic/English status text. Surfaces inside the streaming assistant
 * bubble whenever a long-running pipeline (initial draft, compliance
 * check, review, redraft, improve) is in flight, so the user always
 * knows the system is still working.
 *
 * The bar uses a CSS shimmer animation defined in `harvey-design.css`
 * (`.bayenah-indeterminate-bar`). Track `#E8E8E8`, fill `#111111`.
 *
 * `phase`  : 'draft' | 'compliance' | 'review' | 'redraft' | 'improve'
 * `isArabic`: whether to render the Arabic copy.
 *
 * The counter is driven by the component's own internal interval so
 * callers don't need to track elapsed time.
 */
import React, { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';

const COPY = {
  ar: {
    draft: [
      [0,   'جاري تحليل الطلب وتحديد هيكل المستند...'],
      [40,  'جاري صياغة البنود القانونية...'],
      [80,  'جاري مراجعة الجودة القانونية...'],
      [120, 'المراجعة النهائية — المسودة تكتمل قريباً...'],
    ],
    compliance: [
      [0,   'جاري التحليل...'],
      [60,  'التحليل القانوني يستغرق وقتاً — لا تزال المعالجة جارية...'],
      [120, 'اكتمل التحليل تقريباً — جاري تجميع النتائج...'],
      [180, 'يستغرق هذا النوع من التحليل عادةً ٣-٤ دقائق. شكراً لصبرك.'],
    ],
    review: [
      [0,   'جاري المراجعة...'],
      [60,  'المراجعة القانونية تستغرق وقتاً — لا تزال المعالجة جارية...'],
      [120, 'اكتملت المراجعة تقريباً — جاري تجميع النتائج...'],
      [180, 'هذا النوع من المراجعة يستغرق عادةً ٣-٤ دقائق. شكراً لصبرك.'],
    ],
    redraft: [
      [0,   'جاري إعادة الصياغة...'],
      [60,  'إعادة الصياغة قيد التقدم — لا تزال المعالجة جارية...'],
      [120, 'تم تحسين معظم البنود — جاري التجميع النهائي...'],
      [180, 'إعادة الصياغة تستغرق عادةً ٣-٤ دقائق. شكراً لصبرك.'],
    ],
    improve: [
      [0,   'جاري التحسين...'],
      [60,  'تحسين الصياغة قيد التقدم — لا تزال المعالجة جارية...'],
      [120, 'اكتمل التحسين تقريباً — جاري تجميع المسودة المحسّنة...'],
      [180, 'تحسين المسودة يستغرق عادةً ٣-٤ دقائق. شكراً لصبرك.'],
    ],
  },
  en: {
    draft: [
      [0,   'Analysing request and outlining the document...'],
      [40,  'Drafting the legal articles...'],
      [80,  'Running legal quality review...'],
      [120, 'Final pass — the draft will be ready shortly...'],
    ],
    compliance: [
      [0,   'Analysing...'],
      [60,  'Legal analysis takes time — still processing...'],
      [120, 'Almost done — assembling results...'],
      [180, 'This kind of analysis usually takes 3–4 minutes. Thanks for your patience.'],
    ],
    review: [
      [0,   'Reviewing...'],
      [60,  'Legal review takes time — still processing...'],
      [120, 'Almost done — assembling results...'],
      [180, 'This kind of review usually takes 3–4 minutes. Thanks for your patience.'],
    ],
    redraft: [
      [0,   'Redrafting...'],
      [60,  'Redrafting in progress — still processing...'],
      [120, 'Most clauses improved — final pass...'],
      [180, 'Redrafting usually takes 3–4 minutes. Thanks for your patience.'],
    ],
    improve: [
      [0,   'Improving...'],
      [60,  'Improving wording — still processing...'],
      [120, 'Almost done — assembling the improved draft...'],
      [180, 'Improving usually takes 3–4 minutes. Thanks for your patience.'],
    ],
  },
};

const ARABIC_DIGITS = ['٠','١','٢','٣','٤','٥','٦','٧','٨','٩'];
function toArabicDigits(n) {
  return String(n).split('').map((c) => /\d/.test(c) ? ARABIC_DIGITS[Number(c)] : c).join('');
}

function elapsedLabel(secs, isArabic) {
  if (isArabic) {
    if (secs < 60) return `${toArabicDigits(secs)} ثانية مضت`;
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return s
      ? `${toArabicDigits(m)} د ${toArabicDigits(s)} ث مضت`
      : `${toArabicDigits(m)} ${m === 1 ? 'دقيقة' : 'دقائق'} مضت`;
  }
  if (secs < 60) return `${secs}s elapsed`;
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return s ? `${m}m ${s}s elapsed` : `${m}m elapsed`;
}

function phaseText(phase, secs, isArabic) {
  const list = (COPY[isArabic ? 'ar' : 'en'][phase] || COPY[isArabic ? 'ar' : 'en'].draft);
  let chosen = list[0][1];
  for (const [thresh, text] of list) {
    if (secs >= thresh) chosen = text;
  }
  return chosen;
}

export function ProgressEstimator({ phase = 'draft', isArabic = true, startedAt = null }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const start = startedAt || now;
  const elapsed = Math.max(0, Math.floor((now - start) / 1000));

  return (
    <div
      className={cn('mt-2', isArabic && 'text-right')}
      style={{ maxWidth: '420px' }}
      data-testid="progress-estimator"
    >
      <div
        className="bayenah-indeterminate-bar"
        role="progressbar"
        aria-busy="true"
        aria-label={isArabic ? 'جاري المعالجة' : 'Processing'}
        data-testid="progress-estimator-bar"
      >
        <span aria-hidden="true" />
      </div>
      <div
        className="font-arabic mt-1.5 leading-relaxed"
        style={{ fontSize: '12px', color: '#666666' }}
        data-testid="progress-estimator-phase"
      >
        {phaseText(phase, elapsed, isArabic)}
      </div>
      <div
        className="mt-0.5"
        style={{ fontSize: '11px', color: '#999999' }}
        data-testid="progress-estimator-elapsed"
      >
        {elapsedLabel(elapsed, isArabic)}
      </div>
    </div>
  );
}

export default ProgressEstimator;
